/**
 * Electrical Installation Certificate (EIC) - PDF Transform
 * Transforms form data into PDF-ready structure
 */
import { ZsCalculator } from '../../utils/electrical/bs7671Tables.js';

const parseDevice = (type, rating, disconnectionTime = '0.4s', isRCD = false) => {
  const typeParts = type.split(' ');
  const base = typeParts[0];
  const variant = typeParts.slice(1).join(' ');
  return {
    type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
    typeVariant: base === 'BS' ? `BS ${variant}` : variant,
    rating: parseInt(rating, 10) || 0,
    disconnectionTime,
    is30mARCD: isRCD
  };
};

export const transformForPDF = (formData) => {
  const circuits = (formData.circuits || []).map(circuit => {
    let maxZs = circuit.maxZs;
    let zsStatus = 'pending';

    // Auto-compute max Zs from device
    if (circuit.protectiveDevice && circuit.deviceRating && !maxZs) {
      maxZs = ZsCalculator.getMaxZs(parseDevice(circuit.protectiveDevice, circuit.deviceRating));
    }

    // Determine Zs compliance
    if (maxZs && circuit.measuredZs) {
      const compliance = ZsCalculator.checkZsCompliance(
        parseFloat(circuit.measuredZs),
        parseFloat(maxZs)
      );
      zsStatus = compliance.status;
    }

    return {
      ...circuit,
      maxZs: maxZs || '',
      zsStatus
    };
  });

  // Determine overall assessment
  const hasUnsatisfactory = circuits.some(c => c.testResult === 'UNSATISFACTORY');
  const overallAssessment = hasUnsatisfactory ? 'UNSATISFACTORY' : 'SATISFACTORY';

  return {
    title: 'ELECTRICAL INSTALLATION CERTIFICATE',
    standard: 'BS 7671:2018+A2:2022 (IET Wiring Regulations 18th Edition)',
    contractor: {
      companyName: formData.contractor?.companyName || '',
      address: formData.contractor?.contractorAddress || '',
      email: formData.contractor?.contractorEmail || '',
      phone: formData.contractor?.contractorPhone || '',
      schemeName: formData.contractor?.schemeName || '',
      schemeReference: formData.contractor?.schemeReference || '',
      engineerName: formData.contractor?.engineerName || '',
      engineerCardNumber: formData.contractor?.engineerCardNumber || '',
      notificationDate: formData.contractor?.notificationDate || ''
    },
    client: {
      name: formData.client?.clientName || '',
      address: formData.client?.clientAddress || '',
      email: formData.client?.clientEmail || '',
      phone: formData.client?.clientPhone || ''
    },
    installation: {
      address: formData.installation?.installationAddress || '',
      description: formData.installation?.installationDescription || '',
      extentLimitation: formData.installation?.extentLimitation || '',
      type: formData.installation?.installationType || '',
      mainSupply: formData.installation?.mainSupply || '',
      mainProtectiveDevice: formData.installation?.mainProtectiveDevice || '',
      mainIsolator: formData.installation?.mainIsolator || ''
    },
    supplyCharacteristics: {
      nominalVoltage: formData.supplyCharacteristics?.nominalVoltage || '230 V',
      externalZe: formData.supplyCharacteristics?.externalZe || '',
      earthingType: formData.supplyCharacteristics?.earthingType || '',
      psc: formData.supplyCharacteristics?.psc || '',
      mainFuseSize: formData.supplyCharacteristics?.mainFuseSize || '',
      mainBonding: formData.supplyCharacteristics?.mainBonding || '',
      supplyCable: formData.supplyCharacteristics?.supplyCable || ''
    },
    circuits,
    inspection: formData.inspection || [],
    overallAssessment,
    declaration: {
      certificateNumber: formData.declaration?.certificateNumber || '',
      issueDate: formData.declaration?.issueDate || '',
      engineerSignature: formData.declaration?.engineerSignature || '',
      customerSignature: formData.declaration?.customerSignature || '',
      notes: formData.declaration?.notes || ''
    }
  };
};

export default transformForPDF;